import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../api/config';
import ElevatedButton from '../Layout/ElevatedButton';
import Modal from '../Layout/Modal';

const Container = styled.div`
  height: 100vh;
  display: flex;
  background: ${props => props.theme.background};
  overflow: hidden;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 0.75rem;

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const AdminHeader = styled.div`
  padding: 0.75rem;
  background: ${props => props.theme.background};

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const AdminTitle = styled.h1`
  color: ${props => props.theme.text};
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin: 0;
  }
`;

const Title = styled.h1`
  color: ${props => props.theme.text};
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
`;

const Section = styled.div`
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);

  @media (max-width: 768px) {
    padding: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.text};
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
`;

const AdminGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const SettingCard = styled.div`
  padding: 1.25rem;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.cardBackground};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  margin-bottom: 1rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.cardBackgroundHover};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.16);
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1.25rem;
  margin: 0.5rem 0;
  border: 1px solid ${props => props.theme.inputBorder};
  border-radius: 4px;
  font-size: 1rem;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.text};
  transition: all 0.2s ease;
  box-sizing: border-box;

  &::placeholder {
    color: ${props => props.theme.textMuted};
  }

  &:focus {
    outline: none;
    border-color: #5865f2;
    background: ${props => props.theme.inputBackgroundFocus};
    box-shadow: 0 0 0 2px rgba(88, 101, 242, 0.2);
  }
`;

const ErrorMessage = styled.div`
  padding: 0.75rem;
  margin: 0.5rem 0;
  background: ${props => props.theme.error};
  color: white;
  border-radius: 6px;
  text-align: center;
  border: 1px solid ${props => props.theme.border};
`;

const SuccessMessage = styled.div`
  padding: 0.75rem;
  margin: 0.5rem 0;
  background: ${props => props.theme.success};
  color: white;
  border-radius: 6px;
  text-align: center;
  border: 1px solid ${props => props.theme.border};
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 1rem;
`;

const PageButton = styled.button`
  background: ${props => props.active ? props.theme.buttonPrimary : props.theme.cardBackground};
  color: ${props => props.theme.text};
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? props.theme.buttonPrimaryHover : props.theme.background};
  }

  &:disabled {
    background: ${props => props.theme.border};
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const PasswordRequirements = styled.div`
  margin-top: 0.5rem;
  padding: 0.75rem;
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  font-size: 0.875rem;
  color: ${props => props.theme.textMuted};
`;

const RequirementItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CheckIcon = styled.span`
  color: ${props => props.valid ? props.theme.success : props.theme.error};
  font-weight: bold;
`;

const AdminSettings = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '' });
  const [deleteEmail, setDeleteEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [premiumUserId, setPremiumUserId] = useState('');
  const [customDays, setCustomDays] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [showUserDataModal, setShowUserDataModal] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);

  const fetchAllUsers = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_USERS, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setAllUsers(data.users);
      } else {
        console.error('Failed to fetch users:', response.status, data);
        setAllUsers([]);
      }
    } catch (err) {
      console.error('Fetch users error:', err);
      setAllUsers([]);
    }
  }, []);

  const fetchUserData = async (userId) => {
    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.ADMIN_USER_BY_ID(userId), {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setUserData(data);
        setSelectedUser(data.user);
        setShowUserDataModal(true);
      } else {
        const errorMsg = data.error || 'Failed to fetch user data';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      const errorMsg = 'Network error while fetching user data';
      setError(errorMsg);
      toast.error(errorMsg);
      console.error('Fetch user data error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    setError('');
    setSuccess('');

    if (!newUser.name || !newUser.email || !newUser.password) {
      setError('Please fill in all fields');
      return;
    }

    // Validate password requirements
    const password = newUser.password;
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(password)) {
      setError('Password must contain at least one uppercase letter');
      return;
    }
    if (!/[a-z]/.test(password)) {
      setError('Password must contain at least one lowercase letter');
      return;
    }
    if (!/\d/.test(password)) {
      setError('Password must contain at least one number');
      return;
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      setError('Password must contain at least one special character');
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_USERS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(newUser)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('User created successfully');
        setNewUser({ name: '', email: '', password: '' });
        fetchAllUsers(); // Refresh user list
      } else {
        const errorMsg = data.error || 'Failed to create user';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      const errorMsg = 'Network error while creating user';
      setError(errorMsg);
      toast.error(errorMsg);
      console.error('Create user error:', err);
    }
  };

  const handleDeleteUser = async () => {
    setError('');
    setSuccess('');

    if (!deleteEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    if (deleteEmail === 'admin@planmysemester.com') {
      setError('Cannot delete admin account');
      return;
    }

    // Find user by email
    const userToDelete = allUsers.find(u => u.email === deleteEmail.trim());
    if (!userToDelete) {
      setError('User not found');
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_USER_BY_ID(userToDelete.id), {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('User deleted successfully');
        setDeleteEmail('');
        setUserData(null);
        setSelectedUser(null);
        fetchAllUsers(); // Refresh user list
      } else {
        const errorMsg = data.error || 'Failed to delete user';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      const errorMsg = 'Network error while deleting user';
      setError(errorMsg);
      toast.error(errorMsg);
      console.error('Delete user error:', err);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setError('');
    setSuccess('');

    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_USER_ROLE(userId), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(`User role updated to ${newRole} successfully`);
        fetchAllUsers(); // Refresh user list
        // Refresh selected user data if it's the same user
        if (selectedUser && selectedUser.id === userId) {
          fetchUserData(userId);
        }
        // Refresh current user data in case it was changed
        refreshUser();
      } else {
        const errorMsg = data.error || 'Failed to update user role';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      const errorMsg = 'Network error while updating user role';
      setError(errorMsg);
      toast.error(errorMsg);
      console.error('Role change error:', err);
    }
  };

  const handleAddPremiumTime = async (days) => {
    setError('');
    setSuccess('');

    if (!premiumUserId) {
      setError('Please select a user');
      return;
    }

    if (!days || days < 1 || days > 27375) {
      setError('Please enter a valid number of days (1-27375)');
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_USER_SUBSCRIPTION(premiumUserId), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ 
          subscriptionType: selectedPlan || 'yearly', // Use selected plan or default to yearly
          customDays: days 
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(`Successfully added ${days} days of premium time to user`);
        setPremiumUserId('');
        setCustomDays('');
        setSelectedPlan('');
        fetchAllUsers(); // Refresh user list
      } else {
        toast.error(data.error || 'Failed to add premium time');
      }
    } catch (err) {
      toast.error('Network error while adding premium time');
      console.error('Add premium time error:', err);
    }
  };

  const handleDowngradeToFree = async () => {
    if (!premiumUserId) {
      const errorMsg = 'Please select a user first';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_USER_SUBSCRIPTION(premiumUserId), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ 
          subscriptionType: 'free'
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Successfully downgraded user to free plan');
        setPremiumUserId('');
        setSelectedPlan('');
        fetchAllUsers(); // Refresh user list
      } else {
        const errorMsg = data.error || 'Failed to downgrade user to free plan';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      const errorMsg = 'Network error while downgrading user';
      setError(errorMsg);
      toast.error(errorMsg);
      console.error('Downgrade to free error:', err);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(allUsers.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const endIndex = startIndex + usersPerPage;
  const currentUsers = allUsers.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleDownloadData = () => {
    if (!userData) return;

    const dataStr = JSON.stringify(userData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `user-data-${userData.user.email}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setSuccess('User data downloaded successfully');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleResetTokens = async () => {
    if (!selectedUser) return;

    if (!window.confirm(`Are you sure you want to reset AI tokens for ${selectedUser.name} (${selectedUser.email})? This will reset their monthly usage to 0.`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.ADMIN_RESET_TOKENS(selectedUser.id), {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess(`AI tokens reset successfully for ${selectedUser.name}`);
        // Refresh user data to show updated token usage
        await fetchUserData(selectedUser.id);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Failed to reset tokens');
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      console.error('Error resetting tokens:', error);
      setError('Failed to reset tokens. Please try again.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailablePlans = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.SUBSCRIPTION_PLANS, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const plansData = await response.json();

      if (response.ok) {
        // Parse plans object (same format as SubscriptionModal)
        // Format: { monthly: {...}, quarterly: {...}, yearly: {...}, ai_light: {...}, etc }
        const plansArray = Object.entries(plansData).map(([planKey, planData]) => {
          // Calculate days based on plan type
          let days = 30; // default
          let name = planKey;
          
          if (planKey === 'monthly') {
            days = 30;
            name = 'Monthly Plan';
          } else if (planKey === 'quarterly') {
            days = 90;
            name = 'Quarterly Plan';
          } else if (planKey === 'yearly') {
            days = 365;
            name = 'Yearly Plan';
          } else if (planKey.startsWith('ai_')) {
            days = 30; // AI plans are monthly
            const tier = planKey.replace('ai_', '').replace('_', ' ');
            name = `AI ${tier.charAt(0).toUpperCase() + tier.slice(1)} Plan`;
          }

          return {
            id: planKey, // Use plan key (e.g., 'monthly', 'yearly', 'ai_light') as ID instead of priceId
            name: name,
            description: `${name} - ${planData.interval}`,
            type: planKey.startsWith('ai_') ? 'ai_plan' : 'subscription',
            days: days,
            priceId: planData.priceId,
            price: planData.price,
            interval: planData.interval,
            intervalCount: planData.intervalCount
          };
        });

        // Add Free option at the beginning
        plansArray.unshift({
          id: 'free',
          name: 'Free',
          description: 'Free plan with basic features',
          type: 'free',
          days: 0,
          priceId: null,
          price: 0,
          interval: null,
          intervalCount: null
        });

        setAvailablePlans(plansArray);
      } else {
        console.error('Failed to fetch subscription plans:', response.status, plansData);
        setAvailablePlans([]);
      }
    } catch (error) {
      console.error('Fetch available plans error:', error);
      setAvailablePlans([]);
    }
  };

  const handleExportAllUsers = () => {
    const exportData = {
      exportedBy: user.email,
      exportDate: new Date().toISOString(),
      totalUsers: allUsers.length,
      users: allUsers.map(u => ({
        id: u.id,
        name: u.name,
        email: u.email,
        createdAt: u.createdAt,
        twoFactorEnabled: u.twoFactorEnabled,
        courseCount: u.courseCount,
        assignmentCount: u.assignmentCount
      }))
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `all-users-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setSuccess('All users data exported successfully');
    setTimeout(() => setSuccess(''), 3000);
  };

  // Fetch all users on component mount - only if user has access
  useEffect(() => {
    const isDevAccount = user?.email === 'admin@planmysemester.com';
    if (user && user.role === 'admin' && (user.twoFactorEnabled || isDevAccount)) {
      fetchAllUsers();
    }
  }, [user, fetchAllUsers]);

  // Fetch available plans on component mount - only if user has admin access
  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchAvailablePlans();
    }
  }, [user]);

  // Show loading state while user is being loaded
  if (!user) {
    return (
      <Container>
        <Title>Admin Settings</Title>
        <Section>
          <SettingCard>
            <p style={{ color: 'var(--color-muted)' }}>Loading...</p>
          </SettingCard>
        </Section>
      </Container>
    );
  }

  // Check if user has admin role
  if (user.role !== 'admin') {
    return (
      <Container>
        <Title>Admin Settings</Title>
        <Section>
          <SettingCard>
            <h3 style={{ color: 'var(--color-error)', marginBottom: '1rem' }}>⛔ Access Denied</h3>
            <p style={{ color: 'var(--color-muted)', marginBottom: '1.5rem' }}>
              You do not have permission to access admin settings.
            </p>
            <ElevatedButton onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </ElevatedButton>
          </SettingCard>
        </Section>
      </Container>
    );
  }

  // Check if 2FA is enabled (bypass for dev account)
  const isDevAccount = user.email === 'admin@planmysemester.com';
  if (!user.twoFactorEnabled && !isDevAccount) {
    return (
      <Container>
        <Title>Admin Settings</Title>
        <Section>
          <SettingCard>
            <h3 style={{ color: '#dc3545', marginBottom: '1rem' }}>🔒 Two-Factor Authentication Required</h3>
            <p style={{ color: '#a0a0a0', marginBottom: '1.5rem' }}>
              Admin settings require two-factor authentication to be enabled for security purposes.
            </p>
            <p style={{ color: '#a0a0a0', marginBottom: '2rem' }}>
              Please enable 2FA in your account settings before accessing admin functionality.
            </p>
            <ElevatedButton onClick={() => navigate('/settings')}>
              Go to Settings
            </ElevatedButton>
          </SettingCard>
        </Section>
      </Container>
    );
  }

  return (
    <Container>
      <MainContent>
        <AdminHeader>
          <HeaderTop>
            <AdminTitle>Admin Settings</AdminTitle>
          </HeaderTop>
        </AdminHeader>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <AdminGrid>
          {/* Left Column */}
          <div>
            <Section>
              <SectionHeader>
                <SectionTitle>System Overview</SectionTitle>
              </SectionHeader>
              <SettingCard>
                <p><strong>Total Users:</strong> {allUsers.length}</p>
                <p><strong>Admin Account:</strong> {user.email}</p>
                <p><strong>Last Login:</strong> {new Date().toLocaleString()}</p>
                <p><strong>Current Page:</strong> {currentPage} of {totalPages}</p>
                <ElevatedButton onClick={handleExportAllUsers} style={{ marginTop: '1rem' }}>
                  Export All Users Data (GDPR)
                </ElevatedButton>
              </SettingCard>
            </Section>

            <Section>
              <SectionHeader>
                <SectionTitle>Create New User</SectionTitle>
              </SectionHeader>
              <SettingCard>
                <Input
                  type="text"
                  placeholder="Full Name"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  autoComplete="off"
                />
                <Input
                  type="email"
                  placeholder="Email Address"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  autoComplete="off"
                />
                <Input
                  type="password"
                  placeholder="Password (min 8 characters)"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  autoComplete="off"
                />
                <PasswordRequirements>
                  <div style={{ marginBottom: '0.5rem', fontWeight: '600', color: 'var(--color-text)' }}>Password Requirements:</div>
                  <RequirementItem>
                    <CheckIcon valid={newUser.password.length >= 8}>✓</CheckIcon>
                    At least 8 characters
                  </RequirementItem>
                  <RequirementItem>
                    <CheckIcon valid={/[A-Z]/.test(newUser.password)}>✓</CheckIcon>
                    At least one uppercase letter
                  </RequirementItem>
                  <RequirementItem>
                    <CheckIcon valid={/[a-z]/.test(newUser.password)}>✓</CheckIcon>
                    At least one lowercase letter
                  </RequirementItem>
                  <RequirementItem>
                    <CheckIcon valid={/\d/.test(newUser.password)}>✓</CheckIcon>
                    At least one number
                  </RequirementItem>
                  <RequirementItem>
                    <CheckIcon valid={/[^A-Za-z0-9]/.test(newUser.password)}>✓</CheckIcon>
                    At least one special character
                  </RequirementItem>
                </PasswordRequirements>
                <ElevatedButton onClick={handleCreateUser}>
                  Create User
                </ElevatedButton>
              </SettingCard>
            </Section>

            <Section>
              <SectionHeader>
                <SectionTitle>Add Premium Time</SectionTitle>
              </SectionHeader>
              <SettingCard>
                <p><strong>Add premium time to user accounts:</strong></p>
                <div style={{ marginTop: '1rem' }}>
                  <select
                    value={premiumUserId}
                    onChange={(e) => setPremiumUserId(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      marginBottom: '1rem',
                      border: '1px solid var(--color-border)',
                      borderRadius: '6px',
                      background: 'var(--color-background-secondary)',
                      color: 'var(--color-text)'
                    }}
                  >
                    <option value="" style={{ color: 'var(--color-text)' }}>Select a user...</option>
                    {allUsers.map((u) => (
                      <option key={u.id} value={u.id} style={{ color: 'var(--color-text)' }}>
                        {u.name} ({u.email})
                      </option>
                    ))}
                  </select>

                  <select
                    value={selectedPlan}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      marginBottom: '1rem',
                      border: '1px solid var(--color-border)',
                      borderRadius: '6px',
                      background: 'var(--color-background-secondary)',
                      color: 'var(--color-text)'
                    }}
                  >
                    <option value="" style={{ color: 'var(--color-text)' }}>Select a plan...</option>
                    {availablePlans.map((plan) => (
                      <option key={plan.id} value={plan.id} style={{ color: 'var(--color-text)' }}>
                        Grant {plan.name}
                      </option>
                    ))}
                  </select>

                  <ElevatedButton
                    onClick={() => {
                      const selectedPlanData = availablePlans.find(p => p.id === selectedPlan);
                      if (selectedPlanData) {
                        // Check if user has EVER paid for a subscription - prevent premium time addition unless fully downgraded to free
                        const selectedUser = allUsers.find(u => u.id === premiumUserId);
                        if (selectedUser?.stripeSubscriptionId) {
                          // Only allow if user is fully downgraded to free tier (both subscription and aiTier are free)
                          const isFullyFree = selectedUser.subscription === 'free' && (!selectedUser.aiTier || selectedUser.aiTier === 'free');
                          if (!isFullyFree) {
                            const errorMsg = 'Cannot add premium time to user who has paid for a subscription. They must be fully downgraded to free tier first.';
                            setError(errorMsg);
                            toast.error(errorMsg);
                            return;
                          }
                        }
                        
                        // Handle free plan separately
                        if (selectedPlanData.id === 'free') {
                          handleDowngradeToFree();
                        } else {
                          handleAddPremiumTime(selectedPlanData.days);
                        }
                      }
                    }}
                    disabled={!premiumUserId || !selectedPlan}
                    style={{ 
                      fontSize: '0.9rem', 
                      padding: '0.5rem 1rem',
                      background: 'var(--color-success)',
                      marginBottom: '1rem',
                      opacity: (!premiumUserId || !selectedPlan) ? 0.5 : 1
                    }}
                  >
                    Apply Plan
                  </ElevatedButton>

                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <Input
                      type="number"
                      placeholder="Custom days (1-27375)"
                      value={customDays}
                      onChange={(e) => setCustomDays(e.target.value)}
                      min="1"
                      max="27375"
                      style={{ flex: 1 }}
                      autoComplete="off"
                    />
                    <ElevatedButton
                      onClick={() => {
                        // Check if user has EVER paid for a subscription - prevent premium time addition unless fully downgraded to free
                        const selectedUser = allUsers.find(u => u.id === premiumUserId);
                        if (selectedUser?.stripeSubscriptionId) {
                          // Only allow if user is fully downgraded to free tier (both subscription and aiTier are free)
                          const isFullyFree = selectedUser.subscription === 'free' && (!selectedUser.aiTier || selectedUser.aiTier === 'free');
                          if (!isFullyFree) {
                            const errorMsg = 'Cannot add premium time to user who has paid for a subscription. They must be fully downgraded to free tier first.';
                            setError(errorMsg);
                            toast.error(errorMsg);
                            return;
                          }
                        }
                        handleAddPremiumTime(parseInt(customDays));
                      }}
                      disabled={!premiumUserId || !customDays || parseInt(customDays) < 1 || parseInt(customDays) > 27375}
                      style={{ 
                        fontSize: '0.9rem', 
                        padding: '0.5rem 1rem',
                        background: 'var(--color-success)',
                        opacity: (!premiumUserId || !customDays || parseInt(customDays) < 1 || parseInt(customDays) > 27375) ? 0.5 : 1
                      }}
                    >
                      Add Custom Days
                    </ElevatedButton>
                  </div>
                </div>
              </SettingCard>
            </Section>

            <Section>
              <SectionHeader>
                <SectionTitle>User Management</SectionTitle>
              </SectionHeader>
              <SettingCard>
                <p><strong>All Registered Users ({allUsers.length}):</strong></p>
                <div style={{ maxHeight: '300px', overflowY: 'auto', marginTop: '1rem' }}>
                  {currentUsers.length === 0 ? (
                    <p>No users found</p>
                  ) : (
                    currentUsers.map((u) => (
                      <div key={u.id} style={{
                        padding: '0.5rem',
                        margin: '0.25rem 0',
                        border: '1px solid var(--color-border)',
                        borderRadius: '4px',
                        background: 'var(--color-input-bg)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <strong>{u.name}</strong> ({u.email})
                          <br />
                          <small>Courses: {u.courseCount} | Assignments: {u.assignmentCount}</small>
                        </div>
                        <ElevatedButton
                          onClick={() => fetchUserData(u.id)}
                          disabled={loading}
                          style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                        >
                          {loading ? 'Loading...' : 'View Data'}
                        </ElevatedButton>
                      </div>
                    ))
                  )}
                </div>
                {totalPages > 1 && (
                  <PaginationContainer>
                    <PageButton
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      ‹ Prev
                    </PageButton>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <PageButton
                        key={page}
                        active={page === currentPage}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </PageButton>
                    ))}
                    <PageButton
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next ›
                    </PageButton>
                  </PaginationContainer>
                )}
              </SettingCard>
            </Section>
          </div>

          {/* Right Column */}
          <div>
            <Section>
              <SectionHeader>
                <SectionTitle>Delete User Account</SectionTitle>
              </SectionHeader>
              <SettingCard>
                <Input
                  type="email"
                  placeholder="Enter user email to delete"
                  value={deleteEmail}
                  onChange={(e) => setDeleteEmail(e.target.value)}
                />
                <ElevatedButton onClick={handleDeleteUser} style={{ background: 'var(--color-error)' }}>
                  Delete User
                </ElevatedButton>
              </SettingCard>
            </Section>

            <Section>
              <SectionHeader>
                <SectionTitle>Role Management</SectionTitle>
              </SectionHeader>
              <SettingCard>
                <p><strong>Manage User Roles:</strong></p>
                <div style={{ maxHeight: '400px', overflowY: 'auto', marginTop: '1rem' }}>
                  {currentUsers.length === 0 ? (
                    <p>No users found</p>
                  ) : (
                    currentUsers.map((u) => (
                      <div key={u.id} style={{
                        padding: '0.75rem',
                        margin: '0.5rem 0',
                        border: '1px solid var(--color-border)',
                        borderRadius: '6px',
                        background: 'var(--color-input-bg)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <strong>{u.name}</strong> ({u.email})
                          <br />
                          <small>
                            Role: <span style={{ 
                              color: u.role === 'admin' ? 'var(--color-success)' : u.role === 'support' ? 'var(--color-primary)' : 'var(--color-muted)' 
                            }}>{u.role}</span> | 
                            Courses: {u.courseCount} | 
                            Assignments: {u.assignmentCount} |
                            2FA: {u.twoFactorEnabled ? '✅' : '❌'}
                          </small>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {u.role !== 'admin' && (
                            <ElevatedButton
                              onClick={() => handleRoleChange(u.id, 'admin')}
                              style={{ 
                                fontSize: '0.8rem', 
                                padding: '0.25rem 0.5rem',
                                background: 'var(--color-success)'
                              }}
                            >
                              Make Admin
                            </ElevatedButton>
                          )}
                          {u.role !== 'support' && u.role !== 'admin' && (
                            <ElevatedButton
                              onClick={() => handleRoleChange(u.id, 'support')}
                              style={{ 
                                fontSize: '0.8rem', 
                                padding: '0.25rem 0.5rem',
                                background: 'var(--color-primary)'
                              }}
                            >
                              Make Support
                            </ElevatedButton>
                          )}
                          {u.role === 'admin' && u.email !== user.email && (
                            <ElevatedButton
                              onClick={() => handleRoleChange(u.id, 'user')}
                              style={{ 
                                fontSize: '0.8rem', 
                                padding: '0.25rem 0.5rem',
                                background: 'var(--color-error)'
                              }}
                            >
                              Remove Admin
                            </ElevatedButton>
                          )}
                          {u.role === 'support' && (
                            <ElevatedButton
                              onClick={() => handleRoleChange(u.id, 'user')}
                              style={{ 
                                fontSize: '0.8rem', 
                                padding: '0.25rem 0.5rem',
                                background: 'var(--color-error)'
                              }}
                            >
                              Remove Support
                            </ElevatedButton>
                          )}
                          <ElevatedButton
                            onClick={() => fetchUserData(u.id)}
                            disabled={loading}
                            style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                          >
                            {loading ? 'Loading...' : 'View Data'}
                          </ElevatedButton>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {totalPages > 1 && (
                  <PaginationContainer>
                    <PageButton
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      ‹ Prev
                    </PageButton>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <PageButton
                        key={page}
                        active={page === currentPage}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </PageButton>
                    ))}
                    <PageButton
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next ›
                    </PageButton>
                  </PaginationContainer>
                )}
              </SettingCard>
            </Section>
          </div>
        </AdminGrid>

        {/* User Data Modal */}
        <Modal
          isOpen={showUserDataModal}
          onClose={() => {
            setShowUserDataModal(false);
            setUserData(null);
            setSelectedUser(null);
          }}
          title={`User Details: ${selectedUser?.name || 'Loading...'}`}
        >
          {selectedUser && userData && (
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {/* Basic Information */}
              <SettingCard style={{ marginBottom: '1rem' }}>
                <h3 style={{ marginTop: 0, color: 'var(--color-primary)' }}>Basic Information</h3>
                <p><strong>Name:</strong> {selectedUser.name}</p>
                <p><strong>Email:</strong> {selectedUser.email}</p>
                <p><strong>User ID:</strong> {selectedUser.id}</p>
                <p><strong>Join Date:</strong> {new Date(selectedUser.createdAt).toLocaleString()}</p>
                <p><strong>Last Login:</strong> {selectedUser.lastLogin ? new Date(selectedUser.lastLogin).toLocaleString() : 'Never'}</p>
                <p><strong>2FA Enabled:</strong> {selectedUser.twoFactorEnabled ? 'Yes' : 'No'}</p>
              </SettingCard>

              {/* Subscription Information */}
              <SettingCard style={{ marginBottom: '1rem' }}>
                <h3 style={{ marginTop: 0, color: 'var(--color-primary)' }}>Subscription & Plan</h3>
                <p><strong>Plan Type:</strong> {selectedUser.plan || 'Free'}</p>
                <p><strong>Subscription Status:</strong> {selectedUser.subscriptionStatus || 'Inactive'}</p>
                <p><strong>Subscription End Date:</strong> {selectedUser.subscriptionEndDate ? new Date(selectedUser.subscriptionEndDate).toLocaleDateString() : 'N/A'}</p>
                {selectedUser.plan && selectedUser.plan !== 'free' && (
                  <p><strong>Plan Source:</strong> 
                    <span style={{ 
                      color: selectedUser.adminGranted ? '#ff6b6b' : '#51cf66',
                      fontWeight: 'bold'
                    }}>
                      {selectedUser.adminGranted ? 'Admin Granted' : 'Paid Subscription'}
                    </span>
                  </p>
                )}
              </SettingCard>

              {/* AI Usage */}
              <SettingCard style={{ marginBottom: '1rem' }}>
                <h3 style={{ marginTop: 0, color: 'var(--color-primary)' }}>AI Usage</h3>
                <p><strong>AI Tokens Used:</strong> {userData.aiUsage?.totalTokens || 0}</p>
                <p><strong>Monthly Usage:</strong> {userData.aiUsage?.monthlyTokens || 0}</p>
                <p><strong>Current Month:</strong> {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}</p>
              </SettingCard>

              {/* Statistics */}
              <SettingCard style={{ marginBottom: '1rem' }}>
                <h3 style={{ marginTop: 0, color: 'var(--color-primary)' }}>Statistics</h3>
                <p><strong>Total Courses:</strong> {userData.stats.courseCount}</p>
                <p><strong>Total Assignments:</strong> {userData.stats.assignmentCount}</p>
                <p><strong>Completed Assignments:</strong> {userData.stats.completedAssignments || 0}</p>
                <p><strong>Pending Assignments:</strong> {userData.stats.pendingAssignments || 0}</p>
              </SettingCard>

              {/* Courses */}
              {(userData.courses || []).length > 0 && (
                <SettingCard style={{ marginBottom: '1rem' }}>
                  <h3 style={{ marginTop: 0, color: 'var(--color-primary)' }}>Courses ({(userData.courses || []).length})</h3>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '0.5rem' }}>
                    {(userData.courses || []).map((course) => (
                      <div key={course._id} style={{
                        padding: '0.5rem',
                        margin: '0.25rem 0',
                        border: '1px solid var(--color-border)',
                        borderRadius: '4px',
                        background: 'var(--color-bg)'
                      }}>
                        <strong>{course.name}</strong> - {course.code}
                        <br />
                        <small>Instructor: {course.instructor} | Credits: {course.credits} | Grade: {course.grade || 'N/A'}</small>
                      </div>
                    ))}
                  </div>
                </SettingCard>
              )}

              {/* Assignments */}
              {(userData.assignments || []).length > 0 && (
                <SettingCard style={{ marginBottom: '1rem' }}>
                  <h3 style={{ marginTop: 0, color: 'var(--color-primary)' }}>Assignments ({(userData.assignments || []).length})</h3>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '0.5rem' }}>
                    {(userData.assignments || []).map((assignment) => (
                      <div key={assignment._id} style={{
                        padding: '0.5rem',
                        margin: '0.25rem 0',
                        border: '1px solid var(--color-border)',
                        borderRadius: '4px',
                        background: 'var(--color-bg)'
                      }}>
                        <strong>{assignment.title}</strong> - {assignment.status}
                        <br />
                        <small>Due: {new Date(assignment.dueDate).toLocaleDateString()} | Course: {assignment.course?.name} | Priority: {assignment.priority || 'Normal'}</small>
                      </div>
                    ))}
                  </div>
                </SettingCard>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <ElevatedButton onClick={handleDownloadData}>
                  Download User Data (GDPR)
                </ElevatedButton>
                <ElevatedButton
                  onClick={handleResetTokens}
                  disabled={loading}
                  style={{ background: 'var(--color-warning)', color: 'white' }}
                >
                  {loading ? 'Resetting...' : 'Reset AI Tokens'}
                </ElevatedButton>
                <ElevatedButton
                  onClick={() => {
                    setShowUserDataModal(false);
                    setUserData(null);
                    setSelectedUser(null);
                  }}
                  style={{ background: 'var(--color-error)' }}
                >
                  Close
                </ElevatedButton>
              </div>
            </div>
          )}
        </Modal>
      </MainContent>
    </Container>
  );
};

export default AdminSettings;